import SquareIcon from '@mui/icons-material/Square';
import { Grid, Typography, useTheme } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

const LegendItem: React.FC<{ text: string; color: string; panel?: boolean }> = ({ text, color, panel }) => {
  const { classes, cx } = useStyles({ disableBorder: undefined });
  return (
    <div className={classes.legendItem}>
      <SquareIcon className={cx(classes.legendIcon, panel ? classes.legendIconPanel : null)} style={{ color: color }} />
      <Typography className={cx(classes.legendText, panel ? classes.legendTextPanel : null)}>{text}</Typography>
    </div>
  );
};

interface Props {
  hideOwn?: boolean;
  disableBorder?: boolean;
  /** 'default' keeps the legacy legend card, 'panel' renders the two-zone panel design */
  variant?: 'default' | 'panel';
}

const AnalysisResultLegend: React.FC<Props> = ({ hideOwn, disableBorder, variant = 'default' }) => {
  const { classes, cx } = useStyles({ disableBorder });
  const { t } = useTranslation();
  const theme = useTheme();

  const isPanel = variant === 'panel';

  if (isPanel) {
    return (
      <div className={classes.panelLegend}>
        <LegendItem text={t('targetValue')} color={theme.palette.analysisSecondary.main} panel={true} />
        {_.isNil(hideOwn) || !hideOwn ? <LegendItem text={t('currentValue')} color={theme.palette.primary.main} panel={true} /> : null}
      </div>
    );
  }

  return (
    <div className={cx(classes.legend)}>
      <div className={classes.content}>
        <Typography className={classes.title}>{t('meanings')}</Typography>
        <Grid container={true} direction={'column'} spacing={1}>
          <Grid>
            <LegendItem text={t('targetValue')} color={theme.palette.analysisSecondary.main} />
          </Grid>
          {_.isNil(hideOwn) || !hideOwn ? (
            <Grid>
              <LegendItem text={t('currentValue')} color={theme.palette.primary.main} />
            </Grid>
          ) : null}
        </Grid>
      </div>
    </div>
  );
};

const useStyles = makeStyles<{ disableBorder?: boolean }>()((theme, { disableBorder }) => ({
  legend: {
    width: '100%',
    background: '#FFF',
    border: disableBorder ? 'unset' : `2px solid ${theme.palette.backgrounds.main}`,
    padding: '8px',
    display: 'flex',
    justifyContent: 'center',
  },
  panelLegend: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    '& > div': {
      width: 'auto',
    },
  },
  content: {
    width: '100%',
    maxWidth: '300px',
  },
  title: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    marginBottom: '8px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
  },
  legendIcon: {
    marginRight: '16px',
    fontSize: theme.typography.pxToRem(20),
  },
  legendIconPanel: {
    marginRight: '8px',
    fontSize: theme.typography.pxToRem(12),
  },
  legendText: {
    fontSize: theme.typography.pxToRem(11),
  },
  legendTextPanel: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
  },
}));

export default AnalysisResultLegend;
